import {
  AdminHeader,
  AdminSpinner,
  Disclaimer,
  Div,
  GrubconLogoWhiteSimbolSmall,
  H5,
  Img,
  LocationServiceConfig,
  P,
  VDriveLogoSmall,
  VGrubsDisabledIcon,
  VGrubsLogoSmall,
  VTabletLogoSmall,
  centsToDollars,
  formatter,
  getAgentLocationsV2,
  handleSetUrlSearchParams,
  lightTheme,
  restaurantImage,
  reportError,
  Icon,
} from '@vgrubs/components';
import debounce from 'lodash.debounce';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import axios, { CancelTokenSource } from 'axios'; // Axios for checking if requests were canceled

export const SignUpLocations = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<Array<LocationServiceConfig>>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [ref, inView] = useInView();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('query');

  const navigate = useNavigate();

  const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null); // Track the cancel token

  const { id } = useParams() as { id: string };

  const cancelPreviousRequest = () => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Previous request canceled.');
    }
    cancelTokenSourceRef.current = axios.CancelToken.source(); // Create a new cancel token
  };

  async function getAllLocations(search, page, hasMore) {
    if (!hasMore) return;

    cancelPreviousRequest(); // Cancel any ongoing request before making a new one

    try {
      setLoading(true);
      const { data } = await getAgentLocationsV2(id, {
        limit: 20,
        page: page,
        // query: query?.length > 0 ? query : null,
      });

      if (!search) {
        if (data.data[0]) {
          setHasMore(true);
          setUsers((prevItems) => [...prevItems, ...data.data]);
        } else {
          setHasMore(false);
        }
      } else {
        if (data.data[0]) {
          setHasMore(true);
          setUsers(() => [...data.data]);
        } else {
          setUsers(() => [...data.data]);
          setHasMore(false);
        }
      }

      setPage((prevPage) => prevPage + 1);
      setLoading(false);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      } else {
        reportError(error);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setHasMore(true);
    setPage(1);
    getAllLocations(true, 1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (inView && users.length > 0) {
      getAllLocations(false, page, hasMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const handleChange = async (e) => {
    handleSetUrlSearchParams('query', e.target.value, setSearchParams);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  const handleOnLocation = (id) => {
    navigate(`/locations/${id}`);
  };

  return (
    <>
      {loading && <AdminSpinner />}
      <AdminHeader title={'Signup Locations'} />

      <Div $mt="40"></Div>

      {users.map(
        (
          {
            location,
            vGrubsService,
            vDriveService,
            vOrdersService,
            vRevenueService,
            revenueRecoveryFee,
            vGrubsCommission,
            vDriveCommission,
            vOrdersAgentCommission,
          },
          i
        ) => {
          return (
            <Div
              key={i}
              $flex
              $alignCenter
              $between
              $box
              $p="12px 24px"
              $mb="8"
              $gap="16px"
              $pointer
              onClick={() => handleOnLocation(location.id)}
            >
              <Div>
                <H5 $mb="4">
                  <span>{location?.name}</span>
                </H5>
                <P $m="0" $color={lightTheme.lightGray}>
                  {location?.address}
                </P>
              </Div>
              <Div $flex $alignCenter $gap="24px" $gapSM="8px" $flexNone>
                {vGrubsService && (
                  <ServiceFee
                    logo={<VGrubsLogoSmall />}
                    amount={vGrubsCommission}
                  />
                )}
                {vOrdersService && (
                  <ServiceFee
                    logo={<VTabletLogoSmall />}
                    amount={vOrdersAgentCommission}
                  />
                )}
                {vDriveService && (
                  <ServiceFee
                    logo={<VDriveLogoSmall />}
                    amount={vDriveCommission}
                  />
                )}

                {/* No agent commission on Revenue Recovery: its monthly fee
                    shows instead, and it can be the location's only service. */}
                {vRevenueService && (
                  <ServiceFee
                    logo={<GrubconLogoWhiteSimbolSmall />}
                    amount={revenueRecoveryFee}
                    suffix="/mo"
                  />
                )}

                {!vDriveService &&
                  !vOrdersService &&
                  !vGrubsService &&
                  !vRevenueService && (
                    <Div $flex $alignCenter $gap="8px" $flexNone>
                      <VGrubsDisabledIcon />
                      <Disclaimer $m="0" $color={lightTheme.lightGray}>
                        No Services
                      </Disclaimer>
                    </Div>
                  )}
              </Div>
            </Div>
          );
        }
      )}

      {users.length === 0 && !loading && (
        <Div
          $flex
          $center
          $column
          $height="calc(100vh - 300px)"
          $heightSM="calc(100vh - 300px)"
        >
          <Img
            src={restaurantImage}
            alt="restaurant"
            $width="250px"
            $widthSM="150px"
          />
          <H5>No Locations</H5>
        </Div>
      )}

      <div ref={ref}></div>
    </>
  );
};

const ServiceFee = ({
  logo,
  amount,
  suffix = '',
}: {
  logo: ReactNode;
  amount: number | null;
  suffix?: string;
}) => (
  <Div $flex $column $alignCenter $gap="4px" $flexNone>
    <Icon $size={40} $sizeSM={32}>
      {logo}
    </Icon>
    <Disclaimer $m="0" $color={lightTheme.lightGray}>
      {formatter.format(centsToDollars(amount || 0))}
      {suffix}
    </Disclaimer>
  </Div>
);
